/**
 * SmartImprove Phase 6.5A — Sites + Enhancement Groups sections.
 *
 * Two collapsible cards designed to drop into the existing Client Inputs tab
 * without disturbing any existing layout. Built with the same primitives the
 * other Client Inputs sections use (lucide icons + Tailwind + simple table),
 * so the workspace continues to feel native and consistent.
 *
 * Backward-compat philosophy:
 *   • Both sections are visually optional — they expose an "Add" affordance
 *     but no nag when the engagement doesn't model sites/groups.
 *   • Deletion cascades to NULL on linked child rows (handled by the backend).
 *   • Read-only when `isFinalized=true`.
 */
import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Building2, Layers3, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  listSites,
  addSite,
  deleteSite,
  listGroups,
  addGroup,
  deleteGroup,
} from "../../lib/smartimproveApi";

function SectionShell({ icon: Icon, title, count, defaultOpen = false, locked, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm"
      data-testid={`si-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <header
        className="px-4 py-2.5 border-b border-[#E5E7EB] flex items-center justify-between cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />
          )}
          {Icon ? <Icon className="w-4 h-4 text-[#025979]" /> : null}
          <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
          <span className="ml-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
            {count} record{count === 1 ? "" : "s"}
          </span>
          {locked ? (
            <span className="ml-1 text-[10px] font-semibold text-[#92400E] uppercase tracking-wider">
              locked
            </span>
          ) : null}
        </div>
      </header>
      {open ? <div className="px-4 py-3">{children}</div> : null}
    </section>
  );
}

/* ---------------------------------------------------------------------- */
/* Sites                                                                  */
/* ---------------------------------------------------------------------- */
export function SitesSection({ engagementId, isFinalized, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    site_code: "",
    warehouse_name: "",
    location_city: "",
    country: "",
    warehouse_type: "",
    sap_landscape: "",
    ewm_version: "",
    notes: "",
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listSites(engagementId);
      setRows(r || []);
    } catch {
      toast.error("Failed to load sites");
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submit = async () => {
    if (!draft.site_code.trim() || !draft.warehouse_name.trim()) {
      toast.error("site_code and warehouse_name are required");
      return;
    }
    try {
      await addSite(engagementId, draft);
      toast.success(`Added ${draft.site_code.toUpperCase()}`);
      setDraft({
        site_code: "",
        warehouse_name: "",
        location_city: "",
        country: "",
        warehouse_type: "",
        sap_landscape: "",
        ewm_version: "",
        notes: "",
      });
      setAdding(false);
      await reload();
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add site");
    }
  };

  const remove = async (siteId) => {
    if (!window.confirm("Delete this warehouse? Linked rows will be detached but kept.")) return;
    try {
      await deleteSite(engagementId, siteId);
      toast.success("Site removed");
      await reload();
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove site");
    }
  };

  return (
    <SectionShell
      icon={Building2}
      title="Warehouses / Sites"
      count={rows.length}
      defaultOpen={rows.length > 0}
      locked={isFinalized}
    >
      {rows.length === 0 ? (
        <p className="text-xs text-[#6B7280] mb-3">
          Optional. Add the warehouses or sites this engagement spans (e.g. Dublin DC,
          Cork Manufacturing Warehouse). Issues, KPIs, and observations can be optionally
          tagged to a site so the executive summary reasons across the network.
        </p>
      ) : (
        <table className="w-full text-xs mb-3" data-testid="sites-table">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">Code</th>
              <th className="px-2 py-1.5 text-left font-semibold">Warehouse</th>
              <th className="px-2 py-1.5 text-left font-semibold">Location</th>
              <th className="px-2 py-1.5 text-left font-semibold">Type</th>
              <th className="px-2 py-1.5 text-left font-semibold">SAP / EWM</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-[#F1F5F9]" data-testid={`site-row-${s.site_code}`}>
                <td className="px-2 py-1.5 font-semibold text-[#111827]">{s.site_code}</td>
                <td className="px-2 py-1.5 text-[#374151]">{s.warehouse_name}</td>
                <td className="px-2 py-1.5 text-[#6B7280]">
                  {[s.location_city, s.country].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="px-2 py-1.5 text-[#374151]">{s.warehouse_type || "—"}</td>
                <td className="px-2 py-1.5 text-[#374151]">
                  {[s.sap_landscape, s.ewm_version].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {!isFinalized ? (
                    <button
                      onClick={() => remove(s.id)}
                      className="text-[#B91C1C] hover:bg-[#FEF2F2] p-1 rounded-sm"
                      data-testid={`site-remove-${s.site_code}`}
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!isFinalized && !adding ? (
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-sm border border-[#025979] text-[#025979] hover:bg-[#F8FAFC]"
          data-testid="add-site-btn"
        >
          <Plus className="w-3 h-3" /> Add Warehouse / Site
        </button>
      ) : null}

      {adding ? (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="add-site-form">
          <input
            value={draft.site_code}
            onChange={(e) => setDraft((s) => ({ ...s, site_code: e.target.value }))}
            placeholder="Code (e.g. DUB01)"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs"
            data-testid="site-input-code"
          />
          <input
            value={draft.warehouse_name}
            onChange={(e) => setDraft((s) => ({ ...s, warehouse_name: e.target.value }))}
            placeholder="Warehouse name"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs sm:col-span-2"
            data-testid="site-input-name"
          />
          <input
            value={draft.warehouse_type}
            onChange={(e) => setDraft((s) => ({ ...s, warehouse_type: e.target.value }))}
            placeholder="Type (DC, Plant, Hub)"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs"
          />
          <input
            value={draft.location_city}
            onChange={(e) => setDraft((s) => ({ ...s, location_city: e.target.value }))}
            placeholder="City"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs"
          />
          <input
            value={draft.country}
            onChange={(e) => setDraft((s) => ({ ...s, country: e.target.value }))}
            placeholder="Country"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs"
          />
          <input
            value={draft.sap_landscape}
            onChange={(e) => setDraft((s) => ({ ...s, sap_landscape: e.target.value }))}
            placeholder="SAP landscape"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs"
          />
          <input
            value={draft.ewm_version}
            onChange={(e) => setDraft((s) => ({ ...s, ewm_version: e.target.value }))}
            placeholder="EWM version"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs"
          />
          <div className="sm:col-span-4 flex items-center gap-2">
            <button
              onClick={submit}
              className="px-3 py-1 text-xs font-semibold rounded-sm bg-[#025979] hover:bg-[#024a63] text-white"
              data-testid="site-save-btn"
            >
              Save Warehouse
            </button>
            <button
              onClick={() => setAdding(false)}
              className="px-3 py-1 text-xs rounded-sm border border-[#E5E7EB] hover:bg-[#F8FAFC]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {loading ? <p className="text-[10px] text-[#9CA3AF] mt-1">Loading…</p> : null}
    </SectionShell>
  );
}

/* ---------------------------------------------------------------------- */
/* Enhancement Groups                                                     */
/* ---------------------------------------------------------------------- */
export function GroupsSection({ engagementId, isFinalized, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    group_code: "",
    title: "",
    process_area: "",
    subprocess: "",
    priority: "medium",
    impact_area: "",
    effort_category: "",
    proposed_solution: "",
    implementation_complexity: "medium",
    estimated_business_value: "",
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listGroups(engagementId);
      setRows(r || []);
    } catch {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submit = async () => {
    if (!draft.group_code.trim() || !draft.title.trim()) {
      toast.error("group_code and title are required");
      return;
    }
    try {
      await addGroup(engagementId, draft);
      toast.success(`Added ${draft.group_code.toUpperCase()}`);
      setDraft({
        group_code: "",
        title: "",
        process_area: "",
        subprocess: "",
        priority: "medium",
        impact_area: "",
        effort_category: "",
        proposed_solution: "",
        implementation_complexity: "medium",
        estimated_business_value: "",
      });
      setAdding(false);
      await reload();
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add group");
    }
  };

  const remove = async (groupId) => {
    if (!window.confirm("Delete this enhancement group? Linked rows will be detached but kept.")) return;
    try {
      await deleteGroup(engagementId, groupId);
      toast.success("Group removed");
      await reload();
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove group");
    }
  };

  return (
    <SectionShell
      icon={Layers3}
      title="Enhancement Groups"
      count={rows.length}
      defaultOpen={rows.length > 0}
      locked={isFinalized}
    >
      {rows.length === 0 ? (
        <p className="text-xs text-[#6B7280] mb-3">
          Optional. Group related enhancements consultancy-style (e.g.{" "}
          <em>IB01 – Inbound Receiving RF</em>, <em>OB01 – Pickface &amp; Wave Management</em>).
          Issues, KPIs, observations, and recommendations can be tagged to a group so the
          executive summary speaks in transformation themes rather than line items.
        </p>
      ) : (
        <table className="w-full text-xs mb-3" data-testid="groups-table">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">Code</th>
              <th className="px-2 py-1.5 text-left font-semibold">Title</th>
              <th className="px-2 py-1.5 text-left font-semibold">Process</th>
              <th className="px-2 py-1.5 text-left font-semibold">Priority</th>
              <th className="px-2 py-1.5 text-left font-semibold">Complexity</th>
              <th className="px-2 py-1.5 text-left font-semibold">Business value</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr
                key={g.id}
                className="border-t border-[#F1F5F9]"
                data-testid={`group-row-${g.group_code}`}
              >
                <td className="px-2 py-1.5 font-semibold text-[#111827]">{g.group_code}</td>
                <td className="px-2 py-1.5 text-[#374151]">{g.title}</td>
                <td className="px-2 py-1.5 text-[#6B7280]">
                  {[g.process_area, g.subprocess].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-2 py-1.5 capitalize text-[#374151]">{g.priority}</td>
                <td className="px-2 py-1.5 capitalize text-[#374151]">{g.implementation_complexity}</td>
                <td className="px-2 py-1.5 text-[#374151]">{g.estimated_business_value || "—"}</td>
                <td className="px-2 py-1.5 text-right">
                  {!isFinalized ? (
                    <button
                      onClick={() => remove(g.id)}
                      className="text-[#B91C1C] hover:bg-[#FEF2F2] p-1 rounded-sm"
                      data-testid={`group-remove-${g.group_code}`}
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!isFinalized && !adding ? (
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-sm border border-[#025979] text-[#025979] hover:bg-[#F8FAFC]"
          data-testid="add-group-btn"
        >
          <Plus className="w-3 h-3" /> Add Enhancement Group
        </button>
      ) : null}

      {adding ? (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="add-group-form">
          <input
            value={draft.group_code}
            onChange={(e) => setDraft((s) => ({ ...s, group_code: e.target.value }))}
            placeholder="Code (e.g. IB01)"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs"
            data-testid="group-input-code"
          />
          <input
            value={draft.title}
            onChange={(e) => setDraft((s) => ({ ...s, title: e.target.value }))}
            placeholder="Title (e.g. Inbound Receiving RF)"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs sm:col-span-3"
            data-testid="group-input-title"
          />
          <input
            value={draft.process_area}
            onChange={(e) => setDraft((s) => ({ ...s, process_area: e.target.value }))}
            placeholder="Process area (Inbound/Outbound…)"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs sm:col-span-2"
          />
          <select
            value={draft.priority}
            onChange={(e) => setDraft((s) => ({ ...s, priority: e.target.value }))}
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs bg-white"
          >
            <option value="low">Priority: low</option>
            <option value="medium">Priority: medium</option>
            <option value="high">Priority: high</option>
            <option value="critical">Priority: critical</option>
          </select>
          <select
            value={draft.implementation_complexity}
            onChange={(e) => setDraft((s) => ({ ...s, implementation_complexity: e.target.value }))}
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs bg-white"
          >
            <option value="low">Complexity: low</option>
            <option value="medium">Complexity: medium</option>
            <option value="high">Complexity: high</option>
          </select>
          <input
            value={draft.estimated_business_value}
            onChange={(e) => setDraft((s) => ({ ...s, estimated_business_value: e.target.value }))}
            placeholder="Estimated business value (e.g. $250K/yr)"
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs sm:col-span-4"
          />
          <textarea
            value={draft.proposed_solution}
            onChange={(e) => setDraft((s) => ({ ...s, proposed_solution: e.target.value }))}
            placeholder="Proposed solution (optional, consulting-grade summary)"
            rows={2}
            className="rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs sm:col-span-4"
          />
          <div className="sm:col-span-4 flex items-center gap-2">
            <button
              onClick={submit}
              className="px-3 py-1 text-xs font-semibold rounded-sm bg-[#025979] hover:bg-[#024a63] text-white"
              data-testid="group-save-btn"
            >
              Save Group
            </button>
            <button
              onClick={() => setAdding(false)}
              className="px-3 py-1 text-xs rounded-sm border border-[#E5E7EB] hover:bg-[#F8FAFC]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {loading ? <p className="text-[10px] text-[#9CA3AF] mt-1">Loading…</p> : null}
    </SectionShell>
  );
}
